import { useState, useEffect } from 'react'
import { Button } from '@/components/catalyst-ui/button'
import { Heading } from '@/components/catalyst-ui/heading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/catalyst-ui/table'
import { Badge } from '../../../components/catalyst-ui/badge'
import copyIcon from "../../../assets/icons/16/copy.svg"
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { useCheckUserDomainStatus, useGetThirdPartyDomainDetails, useDeleteThirdPartyDomain } from '../hooks/useMyDomains'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { showSimpleSuccess, showSimpleError } from '@/lib/toast.jsx'
import { usePermissions } from '@/hooks/usePermissions'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { PinwheelLoader } from '@/components/ui/PinwheelLoader'
import DeleteDomainDialog from '@/features/domains/components/DeleteDomainDialog'


const DNS_IP_ADDRESS = '5.78.153.244'
const AI_COMPANION_IP_ADDRESS = '77.42.30.191'
const AI_COMPANION_CNAME = 'cdsn.app'

// Helper to detect subdomain info from domain name
const getSubdomainInfo = (domain) => {
  if (!domain) return { isSubdomain: false, isWwwSubdomain: false, subdomain: null }
  const parts = domain.split('.')
  if (parts.length > 2) {
    const subdomain = parts[0]
    if (subdomain === 'www') {
      return { isSubdomain: true, isWwwSubdomain: true, subdomain: 'www' }
    }
    return { isSubdomain: true, isWwwSubdomain: false, subdomain }
  }
  return { isSubdomain: false, isWwwSubdomain: false, subdomain: null }
}

// Get base (root) domain from any domain (e.g. www.example.com → example.com)
const getBaseDomain = (domain) => {
  if (!domain) return ''
  const parts = domain.split('.')
  if (parts.length > 2) return parts.slice(1).join('.')
  return domain
}

export default function ThirdPartyRecords() {
  const [searchParams] = useSearchParams()
  const domainName = searchParams.get('domain')
  const workspace = searchParams.get('workspace')
  const navigate = useNavigate()
  const { user } = useAuth()
  const checkUserDomainStatus = useCheckUserDomainStatus()
  const { hasRole, isOwnerOrAdmin } = usePermissions()
  const isViewer = hasRole('viewer')
  const canDelete = isOwnerOrAdmin
  const deleteMutation = useDeleteThirdPartyDomain()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [validationStatus, setValidationStatus] = useState(null) // { dnsVerified, cnameVerified, message }

  // Fetch domain details
  const { data: domainData, isLoading, isError } = useGetThirdPartyDomainDetails(user?.id, domainName, {
    enabled: !!user?.id && !!domainName
  })

  // Use the domain's own aiCompanionMode flag from the API response
  // (the backend syncs this when connecting a domain to a project)
  const isAiCompanionMode = domainData?.data?.aiCompanionMode ?? false

  // Redirect to config page if domain is already validated
  useEffect(() => {
    if (domainData?.data) {
      const { dnsVerified, txtVerified } = domainData.data
      if (dnsVerified && txtVerified) {
        // Domain already validated, redirect to config page
        navigate(`/domains/my-domains/config/${encodeURIComponent(domainName)}?workspace=${workspace || ''}`, { replace: true })
      }
    }
  }, [domainData, domainName, workspace, navigate])

  // Subdomain detection for DNS record display
  const subdomainInfo = getSubdomainInfo(domainName)
  const baseDomain = getBaseDomain(domainName)
  // Root domain or www subdomain → show CNAME (www) + A record
  // Other subdomains (e.g. shop.example.com) → show CNAME only
  const isRootOrWww = !subdomainInfo.isSubdomain || subdomainInfo.isWwwSubdomain

  // Build DNS records based on project type and domain type
  const buildDnsRecords = () => {
    if (!domainData?.data) return []

    const cnameTarget = isAiCompanionMode ? AI_COMPANION_CNAME : baseDomain
    const aRecordIp = isAiCompanionMode ? AI_COMPANION_IP_ADDRESS : DNS_IP_ADDRESS

    if (isRootOrWww) {
      // Root domain (example.com) or www subdomain (www.example.com)
      // → www CNAME + A record for the root
      return [
        {
          id: 1,
          recordName: 'www',
          type: 'CNAME',
          value: cnameTarget,
          ttl: 600,
          recordKey: 'cname'
        },
        {
          id: 2,
          recordName: '@',
          type: 'A',
          value: aRecordIp,
          ttl: 600,
          recordKey: 'dns'
        }
      ]
    } else {
      // Other subdomains (e.g. shop.example.com) → CNAME only
      return [
        {
          id: 1,
          recordName: subdomainInfo.subdomain,
          type: 'CNAME',
          value: cnameTarget,
          ttl: 600,
          recordKey: 'cname'
        }
      ]
    }
  }

  const dnsRecords = buildDnsRecords()

  // Helper function to get verification status for a record
  const getRecordStatus = (recordKey) => {
    if (!validationStatus) return null;
    if (recordKey === 'dns') return validationStatus.dnsVerified;
    if (recordKey === 'cname') return validationStatus.cnameVerified ?? validationStatus.dnsVerified;
    return null;
  }

  // Helper function to generate human-readable status message
  const getStatusMessage = (dnsVerified) => {
    if (dnsVerified) {
      return 'DNS records verified successfully!';
    }
    return 'DNS verification failed. Please check your DNS settings and try again.';
  }

  const handleDeleteDomain = () => {
    setShowDeleteDialog(true)
  }

  const handleConfirmDelete = () => {
    if (!domainName || !user?.id) {
      showSimpleError('Cannot delete domain: missing required information')
      return
    }

    deleteMutation.mutate(
      {
        userId: user?.id,
        domain: domainName
      },
      {
        onSuccess: () => {
          setShowDeleteDialog(false)
          navigate(`/domains/my-domains?workspace=${workspace || ''}`)
        },
        onError: (error) => {
          console.error('Failed to delete domain:', error)
        }
      }
    )
  }

  // const handleSendEmail = () => {
  //   console.log('Send email to your teammates')
  // }

  const handleValidateDomain = async () => {
    if (!domainName) return;

    // Clear previous status
    setValidationStatus(null);

    try {
      const result = await checkUserDomainStatus.mutateAsync({
        userId: user?.id,
        domain: domainName
      });

      console.log('Validation result:', result);

      // Parse verification status from response
      const data = result.data || result;
      const dnsVerified = data?.dnsVerified ?? false;
      // Backend now sets both dnsVerified and txtVerified to true on success
      const allVerified = dnsVerified && (data?.txtVerified ?? false);
      const message = result.message || null;

      setValidationStatus({
        dnsVerified: allVerified,
        cnameVerified: allVerified,
        message,
        hasError: !allVerified
      });

      if (allVerified) {
        showSimpleSuccess(getStatusMessage(true));
        // Redirect to config page after successful validation
        navigate(`/domains/my-domains/config/${encodeURIComponent(domainName)}?workspace=${workspace || ''}`);
      } else {
        // Show the specific error message from backend if available
        showSimpleError(message || getStatusMessage(false));
      }
    } catch (error) {
      console.error('DNS check failed:', error);

      // Try to parse JSON from error message (API returns JSON as error text)
      let errorData = null;
      try {
        errorData = JSON.parse(error.message);
      } catch {
        // Not JSON, use as-is
      }

      if (errorData && errorData.data) {
        const dnsVerified = errorData.data?.dnsVerified ?? false;
        const errorMessage = errorData.message || 'Verification failed.';

        setValidationStatus({
          dnsVerified,
          cnameVerified: dnsVerified,
          message: errorMessage,
          hasError: true
        });
        showSimpleError(errorMessage);
      } else {
        setValidationStatus({
          dnsVerified: false,
          cnameVerified: false,
          message: 'Verification request failed. Please try again.',
          hasError: true
        });
        showSimpleError('Verification request failed. Please try again.');
      }
    }
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
      showSimpleSuccess('Copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      showSimpleError('Failed to copy to clipboard');
    });
  }

  if (isLoading) {
    return <div className="flex items-center justify-center py-12">
      <PinwheelLoader text="Loading domain records..." />
    </div>;
  }

  if (isError) {
    return <div className="max-w-screen mx-auto bg-white text-center text-[#B91C1C]">Failed to load domain records</div>;
  }

  return (
    <div className="max-w-screen sm:mx-auto bg-white">
      {/* Header */}
      <div className="mb-8 border-b border-[#E5E7EB] ">
        <div className="mb-4">
          <button onClick={() => navigate(`/domains/my-domains?workspace=${workspace || ''}`)} className="text-sm text-[#64748B] hover:text-[#334155] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded">
            ‹ My Domains
          </button>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
          <Heading>{domainName}</Heading>
          <Badge color={domainData?.data?.project ? 'lime' : 'zinc'}>{domainData?.data?.project ? 'Connected' : 'Not Connected'}</Badge>
        </div>

        <div className="text-sm text-[#71717B] mb-2">
          Third-party Domain
        </div>
        {domainData?.data?.project && (
          <div className="text-sm text-[#71717B] mb-6">
            Connected to project: <strong className="text-gray-900">{domainData.data.project.name}</strong>
          </div>
        )}
      </div>

      {/* DNS Records Table */}
      <div className="bg-white flex justify-center overflow-x-auto">
        <Table>
          <TableHead>
            <TableRow className="!text-[#71717B]">
              <TableHeader className="text-left !font-medium px-0 sm:px-8 md:px-16">Record Name</TableHeader>
              <TableHeader className="text-left !font-medium px-0 sm:px-8 md:px-16">Type</TableHeader>
              <TableHeader className="text-left !font-medium px-0 sm:px-8 md:px-12">Value</TableHeader>
              <TableHeader className="text-left font-medium px-0">TTL</TableHeader>
              {validationStatus && (
                <TableHeader className="text-left !font-medium px-4">Status</TableHeader>
              )}
              <TableHeader className="text-left !font-medium w-16"></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {dnsRecords.map((record) => (
              <TableRow key={record.id} className="border-t border-gray-100">
                <TableCell className="py-3 px-0 sm:px-8 md:px-16 text-sm text-gray-900">
                  {record.recordName}
                </TableCell>
                <TableCell className="py-3 px-0 sm:px-8 md:px-16 text-sm text-[#71717B]">
                  {record.type}
                </TableCell>
                <TableCell className="py-3 px-0 sm:px-8 md:px-12 text-sm text-gray-900 font-normal">
                  <div className="flex items-center gap-2">
                    <span className="break-all">{record.value}</span>
                    <Button
                      plain
                      onClick={() => handleCopy(record.value)}
                      className="text-gray-400 hover:text-gray-600 !p-1 flex-shrink-0"
                      title="Copy value"
                    >
                      <img src={copyIcon} alt="Copy" />
                    </Button>
                  </div>
                </TableCell>

                <TableCell className="py-3 px-4 text-sm text-gray-900">
                  {record.ttl}
                </TableCell>

                {validationStatus && (
                  <TableCell className="py-3 px-4 text-sm">
                    <span
                      className={`inline-flex items-center gap-1 cursor-default ${getRecordStatus(record.recordKey) ? 'text-green-600' : 'text-red-600'}`}
                      title={getRecordStatus(record.recordKey) ? `${record.type} record verified` : `${record.type} record verification failed`}
                    >
                      {getRecordStatus(record.recordKey) ? '✓ Verified' : '✗ Failed'}
                    </span>
                  </TableCell>
                )}

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Action Buttons */}
      {!isViewer && (
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-4">
          <Button
            onClick={handleDeleteDomain}
            outline
            className="!text-[#B91C1C] !font-normal"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Domain'}
          </Button>

          <Button
            onClick={handleValidateDomain}
            color="dark"
            disabled={checkUserDomainStatus.isPending}
          >
            {checkUserDomainStatus.isPending ? 'Validating...' : 'Validate Domain'}
          </Button>
        </div>
      )}

      {/* Validation Status Message */}
      {validationStatus && validationStatus.hasError && validationStatus.message && (
        <div className="flex justify-center mt-4">
          <p className="text-sm text-red-600 text-center">{validationStatus.message}</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteDomainDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        domainName={domainName}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
        requireConfirmations={false}
      />
    </div>
  )
}
